import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { AppChooserPage } from '@/pages/AppChooserPage';
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { StudentsPage } from '@/pages/StudentsPage';
import { GateOfficersPage } from '@/pages/GateOfficersPage';
import { AuditPage } from '@/pages/AuditPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { AttendancePage } from '@/pages/AttendancePage';
import { GroupsPage } from '@/pages/GroupsPage';
import { HRPayrollPage } from '@/pages/HRPayrollPage';
import { MODULES, ModulePreviewPage } from '@/pages/ModulePreviewPage';
import { FeesPage } from '@/pages/fees/FeesPage';
import { EventsPage } from '@/pages/events/EventsPage';
import { NoticesPage } from '@/pages/notices/NoticesPage';
import { DismissalBoardPage } from '@/pages/DismissalBoardPage';
import { ParentLayout } from '@/pages/parent/ParentLayout';
import { ParentLoginPage } from '@/pages/parent/ParentLoginPage';
import { CodesPage } from '@/pages/parent/CodesPage';
import { ParentDelegatesPage } from '@/pages/parent/ParentDelegatesPage';
import { ParentHistoryPage } from '@/pages/parent/ParentHistoryPage';
import { ParentFeesPage } from '@/pages/parent/ParentFeesPage';
import { ParentEventsPage } from '@/pages/parent/ParentEventsPage';
import { ParentNoticesPage } from '@/pages/parent/ParentNoticesPage';
import { GateLayout } from '@/pages/gate/GateLayout';
import { GateVerifyPage } from '@/pages/gate/GateVerifyPage';
import { ExamsPage } from '@/pages/exams/ExamsPage';
import { TimetablePage } from '@/pages/timetable/TimetablePage';
import { TimetablePrintPage } from '@/pages/timetable/TimetablePrintPage';
import { MarkEntryPage } from '@/pages/exams/MarkEntryPage';
import { ReportCardsPrintPage } from '@/pages/exams/ReportCardsPrintPage';
import { ParentReportCardPage } from '@/pages/parent/ParentReportCardPage';
import { ParentResultsPage } from '@/pages/parent/ParentResultsPage';
import { ParentTimetablePage } from '@/pages/parent/ParentTimetablePage';

/*
  One deployment, three audiences.

  `/` is the chooser: a parent and a gate officer arrive at the same URL and pick
  which app is theirs. After sign-in, nobody is routed by which form they used —
  they are routed by the role the API returned, so a gate officer who lands on
  the staff login still ends up at the gate screen rather than an admin dashboard
  they cannot use.

  Each area's layout re-checks the role, so a deep link into the wrong app
  bounces to the right one instead of rendering screens whose requests would 403.
*/
export default function App() {
  return (
    <Routes>
      {/* Entry & auth */}
      <Route path="/" element={<AppChooserPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/parent/login" element={<ParentLoginPage />} />

      {/* Parent app */}
      <Route path="/parent" element={<ParentLayout />}>
        <Route index element={<CodesPage />} />
        <Route path="delegates" element={<ParentDelegatesPage />} />
        <Route path="fees" element={<ParentFeesPage />} />
        <Route path="events" element={<ParentEventsPage />} />
        <Route path="notices" element={<ParentNoticesPage />} />
        <Route path="results" element={<ParentResultsPage />} />
        <Route path="timetable" element={<ParentTimetablePage />} />
        <Route path="history" element={<ParentHistoryPage />} />
      </Route>

      {/* Gate app */}
      <Route path="/gate" element={<GateLayout />}>
        <Route index element={<GateVerifyPage />} />
      </Route>

      {/*
        Report cards are a document, not a screen, so they are routed outside
        the dashboard shell — there is no sidebar or command bar to hide from
        the printer, because there is none on the page.
      */}
      <Route path="/exams/:examId/print" element={<ReportCardsPrintPage />} />
      {/* Landscape, because five days do not fit A4 portrait. */}
      <Route path="/timetable/print" element={<TimetablePrintPage />} />
      {/* A parent's own copy — same document, outside the tab bar for the
          same reason the school's sits outside the dashboard shell. */}
      <Route path="/parent/results/:examId/:studentId/print" element={<ParentReportCardPage />} />

      {/* School dashboard */}
      <Route element={<Layout />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/students" element={<StudentsPage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/attendance" element={<AttendancePage />} />
        <Route path="/dismissal" element={<DismissalBoardPage />} />
        <Route path="/staff" element={<GateOfficersPage />} />
        <Route path="/hr-payroll" element={<HRPayrollPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/fees" element={<FeesPage />} />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/notices" element={<NoticesPage />} />
        <Route path="/timetable" element={<TimetablePage />} />
        <Route path="/exams" element={<ExamsPage />} />
        <Route path="/exams/:examId" element={<MarkEntryPage />} />

        {/* Scoped, navigable, not built. Routed from the same spec list the
            screens render from, so a module cannot appear in the sidebar and
            then 404 — which is the one failure a walkthrough cannot survive. */}
        {Object.entries(MODULES).map(([path, module]) => (
          <Route key={path} path={path} element={<ModulePreviewPage module={module} />} />
        ))}
      </Route>

      {/* Unknown URL goes to the chooser, which forwards a signed-in person to
          their own app rather than guessing at the dashboard. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
